import { Address, Country } from "@/types";
import { Ref, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { postNewAddress, updateAddress } from "@/services/Settings";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import TextInput from "@/components/atoms/TextInput";
import {
  addressValidationSchema,
  AddressFormInput,
} from "@/components/pages/Settings/validation/addressValidationSchema";
import AddressTypeEnum from "@/constants/enums/AddressTypeEnum";
import SelectAddressType from "@/components/molecules/SelectInputs/SelectAddressType";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCountries } from "@/services/RegisterCompany";

interface CompanyAddressStepProps {
  onValidationSuccess: () => void;
  missingAddress?: Address;
  companyId: number;
  ref?: Ref<HTMLFormElement>;
}

function CompanyAddressStep({ onValidationSuccess, missingAddress, companyId, ref }: CompanyAddressStepProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: countries } = useGetCountries(user?.language.id as number, {
    enabled: !!user?.language.id,
  });

  const { handleSubmit, control } = useForm<AddressFormInput>({
    resolver: zodResolver(addressValidationSchema(true)),
    mode: "onChange",
    reValidateMode: "onChange",
    values: {
      addressType: missingAddress?.addressType ?? AddressTypeEnum.BILLING,
      address: missingAddress?.address || "",
      place: missingAddress?.place || "",
      postCode: missingAddress?.postCode || "",
      countryId: missingAddress?.country.id.toString() || "578",
    },
  });

  const { mutateAsync: updateAddressMutation } = useMutation({
    mutationFn: async (data: AddressFormInput) => {
      const { countryId, ...rest } = data;
      const newCountry = countries?.find((c) => c.id.toString() === countryId);
      if (newCountry && missingAddress) {
        await updateAddress(companyId, {
          ...rest,
          id: missingAddress.id,
          country: newCountry,
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyAddresses", companyId],
      });
      onValidationSuccess();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const { mutateAsync: createAddress } = useMutation({
    mutationFn: async (data: AddressFormInput) => {
      await postNewAddress(companyId, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyAddresses", companyId],
      });
      onValidationSuccess();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const onSubmit = async (data: AddressFormInput) => {
    if (missingAddress) {
      await updateAddressMutation(data);
    } else {
      await createAddress(data);
    }
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={{ base: 12 }}>
          <Controller
            control={control}
            name="address"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                required
                label={t("common:address")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Controller
            control={control}
            name="addressType"
            render={({ field, fieldState }) => (
              <SelectAddressType
                {...field}
                filterOut={["HOME", "SHIPPING", "VISITING"]}
                id="add-address-addresstype"
                required
                disabled={true}
                label={t("common:addressType")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Controller
            control={control}
            name="countryId"
            render={({ field, fieldState }) => (
              <SelectCountry
                id={"country"}
                valueKey="id"
                defaultCountry={missingAddress?.country.id.toString() || "578"}
                label={t("common:country")}
                labelKey="name"
                required
                value={field.value ?? ""}
                onChange={(val) => field.onChange(val)}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Controller
            control={control}
            name="postCode"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                required
                label={t("common:postCode")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          <Controller
            control={control}
            name="place"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                required
                label={t("common:place")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
      </Grid>
    </form>
  );
}

export default CompanyAddressStep;
